import React from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, MenuItem, Select, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import RewardsAPI from 'api/faber/RewardsAPI'
import NormalText from 'common/Typography/NormalText'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const formikInitialValues = {
  catalogId: '',
  appliedPlanId: '',
  planStatus: '',
  paymentIntent: ''
}

const NewUserPlanSchema = Yup.object().shape({
  catalogId: Yup.string().required('Catalog ID is required'),
  planStatus: Yup.string().required('Plan Status is required'),
  paymentIntent: Yup.string()
    .matches(/^pi_/, 'Payment Intent must start with "pi_"')
    .required('Payment Intent is required')
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

interface Props {
  handleClose: () => void
  userId: string
}

export const NewUserPlanForm = ({ handleClose, userId }: Props) => {
  const faberToken = OrbitSession.get('faberToken')
  const { data: catalogIds } = useQuery(['getCatalogs'], async () =>
    RewardsAPI.getCatalogs(faberToken)
  )

  const { mutate: createUserPlan } = useMutation(
    OneTimePackApi.createUserPlan,
    {
      onSuccess: () => {
        toast.success('User plan created successfully')
        handleClose()
      },
      onError: () => {
        toast.error('Error creating user plan')
      }
    }
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewUserPlanFormikProps) => {
    const allValues = {
      userId: userId,
      catalogId: values.catalogId,
      planStatus: values.planStatus,
      paymentIntent: values.paymentIntent,
      faberToken: faberToken
    }
    createUserPlan(allValues)
  }

  /**
   * Event handlers
   */
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewUserPlanSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue,
        values
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={3}>
              <NormalText isBold>Catalog ID</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                defaultValue=''
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='catalogId'
                label=''
              >
                {catalogIds &&
                  catalogIds.map((catalog) => (
                    <MenuItem key={catalog.id} value={catalog.id}>
                      {catalog.name}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Plan Status</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='planStatus'
                label=''
                value={values.planStatus}
              >
                <MenuItem value='INACTIVE'>INACTIVE</MenuItem>
                <MenuItem value='ACTIVE'>ACTIVE</MenuItem>
                <MenuItem value='TERMINATED'>TERMINATED</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Payment Intent</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'payment-intent'
                  }}
                  name='paymentIntent'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.paymentIntent && touched.paymentIntent)}
                  helperText={
                    errors.paymentIntent && touched.paymentIntent
                      ? errors.paymentIntent
                      : null
                  }
                />
              </Box>
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button autoFocus onClick={submitForm}>
                Save changes
              </Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

interface NewUserPlanFormikProps {
  catalogId: string
  planStatus: string
  paymentIntent: string
}
